/**
 * ChannelCompare · Module B 分渠道游戏投放对比
 *
 * 需求 §4.4.5
 *
 * 结构：
 * - CardCommon 外壳
 *   - title：模块名
 *   - subFilterBeforeElement：渠道多选 + 指标 Segment（titlebar 下方区域）
 *   - operations：line / bar / table 切换（无 pie）
 *   - hasDownload：下载
 * - 主体：图表区（line/bar；table 模式时不渲染图）+ 排名表（始终展示）
 *
 * 数据：
 * - useChannelRanking · 排名表（sum 接口；行=游戏，列=5 metrics；need_link=true 带环比）
 * - useChannelSummary · 柱状图 / 矩阵表（dimension_summary 接口；need_link=true）
 *   默认请求；提供 barList / linkMap / linkTime / max（hover 上期对比 + 进度条基准）
 * - useChannelTimeline · 折线图（timeline 接口）
 *   由 ChannelLineChart 组件内部直接调用，仅 chartType === 'line' 时挂载 → 切到 line 才发请求
 * - 主页面下发 gameMap（cond.materials_config_games）
 * - 渠道下拉抽到独立组件 <ChannelSelect>，渠道数据由 useChannelMap 获取后通过 props 下发
 *
 * 渠道多选协议（与 ADChannel 一致，由 ChannelSelect 内部维护）：
 * - 业务 state `[]`             → 全选（默认）
 * - 业务 state `[UNSELECT_ALL]` → 全不选
 * - 业务 state `[id, ...]`      → 部分选择
 */
import React, { useMemo, useState } from 'react';
import { Segment } from 'tea-component';
import CardCommon from '@op/components/CardCommon';
import {
  lineIcon,
  barIcon,
  tableIcon,
  commonBarHIcon,
} from '@op/constants/chart';
import useIntls from '@opm/hooks/useIntls';
import { IFilter } from '../../types/filter';
import { TMetric } from '../../types/common';
import {
  CHANNEL_METRICS,
  DEFAULT_CHANNEL_METRIC,
  METRIC_METAS,
} from '../../constants/metrics';
import { useGameColorContext } from '../../context/GameColorContext';
import { useChannelMap } from '../../hooks/useChannelMap';
import { DBO_TM } from '@op/utils/format';
import { gotoDownload } from '@opm/pages/Download';
import {
  useChannelRanking,
  useChannelSummary,
} from '../../hooks/useChannelCompare';
import ChannelSelect from './ChannelSelect';
import ChannelLineChart from './ChannelLineChart';
import ChannelBarChart from './ChannelBarChart';
import ChannelStackedBarChart from './ChannelStackedBarChart';
import ChannelShareBarChart from './ChannelShareBarChart';
import ChannelMatrixTable from './ChannelMatrixTable';
import ChannelRankingTable from './ChannelRankingTable';
import { type IGameSortValue, TOTAL_VALUE } from './GameSortSelect';
import './index.scss';
import BubbleCommon from '@op/components/BubbleCommon';
import { getDayMode } from '@op/pages/adCreatives/overview/utils/metricLabels';

export const CHANNEL_COMPARISON_TITLE = 'Channel Comparison';

export interface IChannelCompareProps {
  filter: IFilter;
  id?: string;
  className?: string;
  emptyTitleText: string;
  gotoAdDeliveryOverview?: (id: string) => void;
}

type TChartType = 'line' | 'bar' | 'table' | 'common-bar';

const CHART_HEIGHT = 380;
const CARD_MIN_HEIGHT = 720;

const MAX_SHOW_CHANNEL = 3;

export default function ChannelCompare({
  filter,
  id,
  className,
  emptyTitleText,
  gotoAdDeliveryOverview
}: IChannelCompareProps) {
  const { intl } = useIntls();
  const { gameMap } = useGameColorContext();
  const { channels, allChannelIds, channelMap } = useChannelMap();
  const [chartType, setChartType] = useState<TChartType>('common-bar');
  /** 渠道多选 state（默认 `[]` = 全选；协议见 ChannelSelect 组件文档） */
  const [channelIds, setChannelIds] = useState<string[]>([]);
  const [metric, setMetric] = useState<TMetric>(DEFAULT_CHANNEL_METRIC);
  /**
   * 柱状图按游戏排序（仅 common-bar 模式）
   * - by:   game_id 或 '__total__'（默认按总值排序）
   * - order: 'asc' / 'desc'
   */
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [gameSort, setGameSort] = useState<IGameSortValue>({
    by: TOTAL_VALUE,
    order: 'desc',
  });

  // 排名表数据（sum 接口；5 个 metric 一次拉，metric Segment 切换不重发）
  const {
    list: rankingList,
    fullList: rankingFullList,
    total: rankingTotal,
    linkTime,
    pageInfo,
    setPageInfo,
    sortBy,
    setSortBy,
    loading: rankingLoading,
  } = useChannelRanking({ filter, channelIds });

  // 上方图表数据（dimension_summary 接口；驱动 bar / matrix；need_link=true 提供环比）
  // 折线图走 timeline，由 ChannelLineChart 内部 hook 在 chartType==='line' 时挂载触发请求
  const {
    barList,
    linkMap: summaryLinkMap,
    linkTime: summaryLinkTime,
    dimensionMax: summaryDimensionMax,
    loading: chartLoading,
    matrixSort,
    setMatrixSort,
  } = useChannelSummary({
    filter,
    channelIds,
    metric,
  });

  /** 文案预先固化 */
  const labels = useMemo(
    () => ({
      title: intl(CHANNEL_COMPARISON_TITLE),
      ranking: intl('Summary Ranking of Selected Channels'),
      empty: intl('No data available for the selected period'),
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [intl],
  );
  /** 日期模式：将 getDayMode 结果映射为 METRIC_METAS.label 的 key（daily/weekly/monthly） */
  const dayMode = useMemo(() => {
    const dateType = getDayMode(
      filter.date_type,
      filter.start_time,
      filter.end_time,
    );
    if (dateType === 'weekly' || dateType === 'monthly') {
      return dateType;
    }
    return dateType === 'single' ? 'daily' : 'daily_range'; // single / daily_range → daily
  }, [filter.date_type, filter.start_time, filter.end_time]);

  /** 指标 Segment 选项 */
  const metricOptions = useMemo(
    () =>
      CHANNEL_METRICS.map((m) => {
        const meta = METRIC_METAS[m];
        const labelStr =
          typeof meta.label === 'object' ? meta.label[dayMode] : meta.label;
        return {
          value: m,
          text: (
            <>
              {meta.tipsMap ? (
                <BubbleCommon tipsMap={meta.tipsMap} selected={metric === m}>
                  {intl(labelStr!)}
                </BubbleCommon>
              ) : (
                intl(labelStr!)
              )}
            </>
          ),
        };
      }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [intl, metric, dayMode],
  );

  /** 当前指标 i18n label（用于 download 文件名） */
  const metricLabel = useMemo(() => {
    const meta = METRIC_METAS[metric];
    const labelStr =
      typeof meta.label === 'object' ? meta.label[dayMode] : meta.label;
    return intl(labelStr!);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [intl, metric, dayMode]);

  /** subFilterBeforeElement 区域：渠道多选 + 游戏排序 + 指标 Segment */
  const subFilterBefore = (
    <div className="dbom-channel-compare__sub-filter">
      <ChannelSelect
        value={channelIds}
        onChange={(value) => {
          setChannelIds(value);
          const len = value.length;
          if ((len > MAX_SHOW_CHANNEL || len === 0) && chartType === 'bar') {
            setChartType('common-bar');
          } else if (len <= MAX_SHOW_CHANNEL && chartType === 'common-bar') {
            setChartType('bar');
          }
        }}
        channels={channels}
        allChannelIds={allChannelIds}
        className="dbom-channel-compare__channel-select"
      />
      {/* {chartType === 'common-bar' && (
        <GameSortSelect
          gameIds={filter.game_ids}
          gameMap={gameMap}
          value={gameSort}
          onChange={(v) => setGameSort(v as IGameSortValue)}
          className="dbom-channel-compare__game-sort"
        />
      )} */}
      <Segment
        className="dbom-channel-compare__metric"
        value={metric}
        options={metricOptions}
        onChange={(v) => {
          setMetric(v as TMetric);
          setSortBy({
            by:
              v === 'creative_count_stat_by_channel_percent'
                ? 'creative_count_stat'
                : v,
            order: 'desc',
          });
        }}
      />
    </div>
  );

  /**
   * 渲染上方区域（图表 / 矩阵表）
   * - bar：dimension_summary 数据驱动（默认请求）
   * - table：dimension_summary 数据驱动（行=游戏、列=渠道；含环比 + 按列进度条基准 dimensionMax）
   * - line：ChannelLineChart 内部自带 useChannelTimeline；仅 chartType=line 时挂载触发请求
   */
  const renderChart = () => {
    switch (chartType) {
      case 'bar':
        return (
          <ChannelBarChart
            list={barList}
            gameMap={gameMap}
            channelMap={channelMap}
            metric={metric}
            loading={chartLoading}
            height={CHART_HEIGHT}
            emptyTitleText={emptyTitleText}
            sortByGame={gameSort}
            gameOrder={filter.game_ids}
          />
        );
      case 'common-bar':
        return metric === 'creative_count_stat_by_channel_percent' ? (
          <ChannelShareBarChart
            list={barList}
            gameMap={gameMap}
            channelMap={channelMap}
            metric={metric}
            loading={chartLoading}
            height={CHART_HEIGHT}
            emptyTitleText={emptyTitleText}
            sortByGame={gameSort}
            gameOrder={filter.game_ids}
          />
        ) : (
          <ChannelStackedBarChart
            list={barList}
            gameMap={gameMap}
            channelMap={channelMap}
            metric={metric}
            loading={chartLoading}
            height={CHART_HEIGHT}
            emptyTitleText={emptyTitleText}
            sortByGame={gameSort}
            gameOrder={filter.game_ids}
          />
        );
      case 'line':
        return (
          <ChannelLineChart
            filter={filter}
            channelIds={channelIds}
            gameMap={gameMap}
            metric={metric}
            height={CHART_HEIGHT}
            emptyTitleText={emptyTitleText}
          />
        );
      case 'table':
        return (
          <ChannelMatrixTable
            list={barList}
            linkMap={summaryLinkMap}
            linkTime={summaryLinkTime}
            dimensionMax={summaryDimensionMax}
            metric={metric}
            loading={chartLoading}
            gameMap={gameMap}
            channelMap={channelMap}
            emptyTitleText={emptyTitleText}
            height={280.5}
            sort={matrixSort}
            onSortChange={setMatrixSort}
          />
        );
      default:
        return null;
    }
  };

  return (
    <CardCommon
      id={id}
      className={`dbom-channel-compare-card ${className ?? ''}`}
      title={labels.title}
      // 默认 RenderCard 模式（contentCard=false）下用 subFilterBeforeElement
      // 而不是 subTitle —— subTitle 仅 RenderContentCard 模式有效
      subFilterBeforeElement={subFilterBefore}
      loading={rankingLoading && chartLoading}
      loadingHeight={CARD_MIN_HEIGHT}
      emptyHeight={CARD_MIN_HEIGHT}
      operationInfo={[chartType, setChartType]}
      operations={[commonBarHIcon, lineIcon, barIcon, tableIcon]}
      defaultOperation={chartType}
      // handleOperationsOnClick={(item: operation) => {
      //   setChartType((item?.symbol as TChartType) || 'line');
      // }}
      hasDownload
      handleDownloadOnClick={() => {
        const time = DBO_TM.timeTitle({
          start_time: filter.start_time,
          end_time: filter.end_time,
          date_type: filter.date_type,
        });
        // 下载内容跟当前视图保持一致：
        // - chartType === 'table' → 上方矩阵表（行=游戏、列=渠道；按 matrixSort 应用排序）
        // - chartType === 'line' / 'bar' → 下方排名表（rankingFullList 已按 sortBy 排序）
        if (chartType === 'table') {
          void gotoDownload({
            key: 'MarketingCompetitorAdComparisonChannelMatrix',
            fileName: `Competitor_Ad_Comparison_Channel_Matrix_${metricLabel.replace(
              /\s+/g,
              '_',
            )}_${time}`,
            filter,
            data: barList,
            auxiliaryParams: {
              gameMap,
              metric,
              metricLabel,
              matrixSort,
            },
          });
        } else {
          void gotoDownload({
            key: 'MarketingCompetitorAdComparisonChannel',
            fileName: `Competitor_Ad_Comparison_Channel_Ranking_${time}`,
            filter,
            data: rankingFullList,
            auxiliaryParams: {
              gameMap,
              hasChannelFilter: !!channelIds && channelIds.length > 0,
            },
          });
        }
      }}
    >
      <div className="dbom-channel-compare__chart">{renderChart()}</div>

      <div className="dbom-channel-compare__ranking">
        <div className="dbom-channel-compare__ranking-title">
          {labels.ranking}
        </div>
        <ChannelRankingTable
          list={rankingList}
          total={rankingTotal}
          pageInfo={pageInfo}
          setPageInfo={setPageInfo}
          sortBy={sortBy}
          setSortBy={setSortBy}
          gameMap={gameMap}
          loading={rankingLoading}
          channelIds={channelIds}
          newTime={linkTime.newTime}
          oldTime={linkTime.oldTime}
          gotoAdDeliveryOverview={gotoAdDeliveryOverview}
          emptyTitleText={emptyTitleText}
          dayMode={dayMode}
        />
      </div>
    </CardCommon>
  );
}
